import type { FC } from 'react';
import { useEffect, useState } from 'react';

import { Body } from '@cognite/cogs-core-v11/tokens/typography/typography.tsx';
import { Flex } from '@cognite/cogs-core-v11/components/flex/flex.tsx';
import { Input } from '@cognite/cogs-core-v11/components/input/input.tsx';

import { ComponentContainer } from '../../../../ui/component/component-container/component-container';
import { useUpsertAppConfigMutation } from '../../../hooks/use-mutation/use-upsert-app-config-mutation/use-upsert-app-config-mutation';
import { useAppConfigQuery } from '../../../hooks/use-query/use-app-config-query';
import { SaveConfigurationButton } from '../../../save-configuration/save-configuration-button';
import type { AppConfig } from '../../../types';
import { getIsNewConfigVersion } from '../../../utils/utils';
import * as S from '../elements';

interface Props {
  hasAppConfigPermission: boolean;
  rootLocationExternalId: string;
}

export const RootLocationConfigDataset: FC<Props> = ({
  hasAppConfigPermission,
  rootLocationExternalId,
}) => {
  const [datasetId, setDatasetId] = useState<number>();

  const { data: appConfig, isSuccess, isLoading } = useAppConfigQuery();
  const isNewConfigVersion = getIsNewConfigVersion(appConfig);
  const { mutateAsync: upsertAppConfig } = useUpsertAppConfigMutation();
  const configuredRootLocation =
    appConfig?.featureConfiguration?.rootLocationConfigurations?.find(
      (rootLocation) =>
        rootLocationExternalId ===
        (isNewConfigVersion
          ? rootLocation.externalId
          : rootLocation.assetExternalId)
    );

  useEffect(() => {
    if (isSuccess && configuredRootLocation) {
      if (configuredRootLocation.dataSetId) {
        setDatasetId(configuredRootLocation.dataSetId);
      }
    }
  }, [isSuccess, configuredRootLocation, appConfig]);

  const hasConfigChanged =
    datasetId !== (configuredRootLocation?.dataSetId ?? '');

  const handleSave = async () => {
    if (!appConfig) return;

    const newAppConfig: AppConfig = {
      ...appConfig,
      featureConfiguration: {
        ...appConfig.featureConfiguration,
        rootLocationConfigurations: [
          ...(appConfig.featureConfiguration?.rootLocationConfigurations?.map(
            (rootConfig) => {
              if (
                isNewConfigVersion
                  ? configuredRootLocation?.externalId === rootConfig.externalId
                  : configuredRootLocation?.assetExternalId ===
                    rootConfig.assetExternalId
              ) {
                return {
                  ...rootConfig,
                  dataSetId: datasetId,
                };
              }
              return { ...rootConfig };
            }
          ) || []),
        ],
      },
    };

    await upsertAppConfig({
      newAppConfig,
    });
  };

  const documentCardConfigs = (
    <Flex direction="column" gap={8}>
      <Body size="x-small">
        Timeseries that is created from InField will get a metadata field
        (source=APP) to identify its origin. You can additionally add a dataset
        you want these timeseries to be stored in.
      </Body>
      <Flex gap={16}>
        <Flex direction="column" gap={8}>
          <Body size="x-small" strong>
            Dataset id (internal id) for InField
          </Body>
          <Input
            disabled={!hasAppConfigPermission}
            value={datasetId !== undefined ? datasetId : ''}
            type="number"
            onChange={(e) =>
              setDatasetId(
                !Number.isNaN(e.target.valueAsNumber)
                  ? e.target.valueAsNumber
                  : undefined
              )
            }
          />
          <Body size="x-small">
            The dataset that will hold the files and timeseries created from
            InField.
          </Body>
        </Flex>
      </Flex>
    </Flex>
  );

  return (
    <ComponentContainer>
      <S.Wrapper>
        <Flex direction="column" gap={16}>
          {documentCardConfigs}
          <SaveConfigurationButton
            onSave={handleSave}
            isDisabled={!hasConfigChanged}
            isLoading={isLoading}
          />
        </Flex>
      </S.Wrapper>
    </ComponentContainer>
  );
};
